/* v2.2 report data model in the Studio: which tables this report uses and
 * how they're linked (definition.model, validated by backend
 * query_engine/validator.py ReportModel, applied at query time by
 * backend/services/report_model.py). Links live inside the report, so
 * changing them never affects another report.
 *
 * A link says "these two columns hold the same thing" (e.g. Payroll ·
 * Employee ID = Employees · ID). Then a dropdown or filter on one table
 * also filters charts built on the other one, on every page. */
import { useQueries, useQuery } from "@tanstack/react-query";
import { ArrowLeftRight, ArrowRight, Link2, Plus, Sparkles, Table2, Trash2, X } from "lucide-react";
import { useMemo, useState } from "react";

import { apiRequest } from "../api/client";
import { dataHubApi } from "../api/dataHub";
import { Button, Checkbox, Select } from "../ds";
import { groupDatasets, useDatasets } from "./LeftPanels";
import { uid, type SDef } from "./model";
import { suggestLinks } from "./modelLinks";

export interface ModelLink {
  id: string;
  fromDatasetId: string;
  fromField: string;
  toDatasetId: string;
  toField: string;
  crossFilter?: "single" | "both";
  active?: boolean;
}

export interface ReportModel {
  tables: string[];
  relationships: ModelLink[];
}

interface FieldRow { source_name: string; display_name: string; data_type: string; field_role: string }

/** Tables the report uses: listed in the model + bound by any visual or global filter. */
export function tablesInReport(def: SDef): string[] {
  const ids = new Set<string>((def.model as ReportModel | undefined)?.tables ?? []);
  for (const p of def.pages ?? []) for (const w of p.widgets ?? []) if (w.datasetId) ids.add(w.datasetId);
  for (const g of def.globalFilters ?? []) if (g?.datasetId) ids.add(g.datasetId);
  return [...ids];
}


export function ModelPanel({ mode, def, onChange, activeDatasetId, onPickDataset, linksOnly }: { mode: "admin" | "owner"; def: SDef; onChange: (next: SDef, key?: string) => void; activeDatasetId: string | null; onPickDataset: (id: string) => void; /** v2.3: the Data pane shows the tables as a tree; this panel then only does links. */ linksOnly?: boolean }) {
  const datasets = useDatasets(mode);
  const model: ReportModel = { tables: [], relationships: [], ...(def.model ?? {}) };
  const tables = tablesInReport(def);
  const byId = useMemo(() => new Map((datasets.data ?? []).map((d) => [d.id, d])), [datasets.data]);
  const name = (id: string) => byId.get(id)?.name ?? "Unknown table";
  const base = mode === "admin" ? "/api/v1/admin/datasets" : "/api/v1/owner/datasets";
  const fieldQueries = useQueries({
    queries: tables.map((id) => ({ queryKey: ["studio-fields", mode, id], queryFn: () => apiRequest<FieldRow[]>(`${base}/${id}/fields`), staleTime: 5 * 60_000 })),
  });
  const fieldsOf = (id: string): FieldRow[] => (fieldQueries[tables.indexOf(id)]?.data as FieldRow[] | undefined) ?? [];
  const fieldLabel = (id: string, f: string) => fieldsOf(id).find((x) => x.source_name === f)?.display_name ?? f;

  const usedByVisuals = useMemo(() => {
    const s = new Set<string>();
    for (const p of def.pages ?? []) for (const w of p.widgets ?? []) if (w.datasetId) s.add(w.datasetId);
    for (const g of def.globalFilters ?? []) if (g?.datasetId) s.add(g.datasetId);
    return s;
  }, [def]);

  const setModel = (next: ReportModel, key?: string) => onChange({ ...def, model: next }, key);
  const addTable = (id: string) => { if (!id) return; setModel({ ...model, tables: [...new Set([...model.tables, id])] }); onPickDataset(id); };
  const removeTable = (id: string) => setModel({ tables: model.tables.filter((t) => t !== id), relationships: model.relationships.filter((r) => r.fromDatasetId !== id && r.toDatasetId !== id) });
  const addLink = ({ fromDatasetId, fromField, toDatasetId, toField, crossFilter = "both" }: Omit<ModelLink, "id">) =>
    setModel({ tables: [...new Set([...model.tables, fromDatasetId, toDatasetId])], relationships: [...model.relationships, { id: uid("link"), fromDatasetId, fromField, toDatasetId, toField, crossFilter, active: true }] });
  const patchLink = (id: string, patch: Partial<ModelLink>) => setModel({ ...model, relationships: model.relationships.map((r) => (r.id === id ? { ...r, ...patch } : r)) });
  const removeLink = (id: string) => setModel({ ...model, relationships: model.relationships.filter((r) => r.id !== id) });

  // Suggested links: v2.3 from real values on the server (names AND overlapping
  // values); falls back to "same column name" while loading or if it fails.
  const linkedPairs = model.relationships.map((r) => [r.fromDatasetId, r.toDatasetId] as [string, string]);
  const server = useQuery({
    queryKey: ["studio-link-suggest", mode, [...tables].sort().join("|"), linkedPairs.map((p) => [...p].sort().join("~")).sort().join("|")],
    queryFn: () => dataHubApi.suggestLinks(tables, linkedPairs),
    enabled: tables.length > 1,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const nameOnly = useMemo(
    () => suggestLinks(tables, fieldsOf, linkedPairs).slice(0, 6),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tables.join("|"), model.relationships, fieldQueries.map((q) => q.dataUpdatedAt).join("|")],
  );
  const suggestions: Array<Omit<ModelLink, "id"> & { why: string; confidence?: number }> = server.data
    ? server.data.links
        .filter((l) => !linkedPairs.some(([a, b]) => [a, b].sort().join("|") === [l.fromDatasetId, l.toDatasetId].sort().join("|")))
        .slice(0, 8)
    : nameOnly;

  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<{ a: string; af: string; b: string; bf: string; both: boolean }>({ a: "", af: "", b: "", bf: "", both: true });
  const available = (datasets.data ?? []).filter((d) => !tables.includes(d.id));

  return (
    <div className="st-section" style={linksOnly ? undefined : { borderBottom: "1px solid var(--divider)", paddingBottom: 12, marginBottom: 12 }}>
      {!linksOnly ? (<>
      <div className="st-section__label">Tables in this report</div>
      {tables.length === 0 ? <div className="ds-small ds-faint" style={{ marginBottom: 6 }}>None yet — add the tables you want to build from.</div> : null}
      <div className="ds-stack" style={{ gap: 4 }}>
        {tables.map((id) => (
          <div key={id} className="st-field" style={{ cursor: "pointer", background: id === activeDatasetId ? "var(--accent-soft)" : undefined }} onClick={() => onPickDataset(id)} title="Show its columns">
            <Table2 size={14} className="ds-faint" />
            <span className="ds-grow ds-truncate">{name(id)}</span>
            {!usedByVisuals.has(id) ? (
              <button type="button" className="st-mini" aria-label={`Remove ${name(id)} from this report`} onClick={(e) => { e.stopPropagation(); removeTable(id); }}><X size={12} /></button>
            ) : null}
          </div>
        ))}
      </div>
      <Select inputSize="sm" value="" onChange={(e) => addTable(e.target.value)} style={{ marginTop: 6 }} aria-label="Add a table">
        <option value="">+ Add a table…</option>
        {groupDatasets(available).map(([g, items]) => (
          <optgroup key={g} label={g}>{items.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</optgroup>
        ))}
      </Select>
      </>) : null}

      {tables.length > 1 ? (
        <>
          <div className="st-section__label" style={{ marginTop: linksOnly ? 0 : 14 }}>Links{model.relationships.length ? ` · ${model.relationships.length}` : ""}</div>
          <div className="ds-small ds-faint" style={{ marginBottom: 6 }}>Linked tables filter each other: a dropdown or filter on one also filters the charts built on the other, on every page.</div>
          {model.relationships.map((r) => (
            <div key={r.id} className="st-field" style={{ alignItems: "flex-start", opacity: r.active === false ? 0.55 : 1 }}>
              <Link2 size={14} className="ds-faint" style={{ marginTop: 3 }} />
              <div className="ds-grow" style={{ minWidth: 0, fontSize: 12.5, lineHeight: 1.35 }}>
                <div className="ds-truncate"><strong>{name(r.fromDatasetId)}</strong> · {fieldLabel(r.fromDatasetId, r.fromField)}</div>
                <div className="ds-truncate"><strong>{name(r.toDatasetId)}</strong> · {fieldLabel(r.toDatasetId, r.toField)}</div>
                <div className="ds-small ds-faint" style={{ marginTop: 3 }}>{r.active === false ? "Inactive — does not filter" : (r.crossFilter ?? "both") === "single" ? `${name(r.toDatasetId)} filters ${name(r.fromDatasetId)}` : "Both tables filter each other"}</div>
              </div>
              <button type="button" className="st-mini" title={(r.crossFilter ?? "both") === "both" ? "Filters both ways — click for one way" : `Only ${name(r.toDatasetId)} filters ${name(r.fromDatasetId)} — click for both ways`} aria-label="Change filter direction" onClick={() => patchLink(r.id, { crossFilter: (r.crossFilter ?? "both") === "both" ? "single" : "both" })}>
                {(r.crossFilter ?? "both") === "both" ? <ArrowLeftRight size={13} /> : <ArrowRight size={13} style={{ transform: "rotate(180deg)" }} />}
              </button>
              <button type="button" className="st-mini" aria-label="Remove link" onClick={() => removeLink(r.id)}><Trash2 size={12} /></button>
            </div>
          ))}
          {suggestions.length ? (
            <div style={{ margin: "6px 0" }}>
              {suggestions.map((s) => (
                <div key={`${s.fromDatasetId}|${s.toDatasetId}`} className="st-field" style={{ borderStyle: "dashed" }}>
                  <Sparkles size={13} color="var(--accent)" />
                  <span className="ds-grow" style={{ fontSize: 12.5, minWidth: 0 }} title={s.why}>
                    <span className="ds-truncate" style={{ display: "block" }}>{name(s.fromDatasetId)} ↔ {name(s.toDatasetId)}</span>
                    <span className="ds-faint ds-truncate" style={{ display: "block" }}>{fieldLabel(s.fromDatasetId, s.fromField)} = {fieldLabel(s.toDatasetId, s.toField)} · {s.why}</span>
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => addLink({ fromDatasetId: s.fromDatasetId, fromField: s.fromField, toDatasetId: s.toDatasetId, toField: s.toField, crossFilter: s.crossFilter ?? "both" })}>Link</Button>
                </div>
              ))}
            </div>
          ) : null}
          {adding ? (
            <div className="ds-stack" style={{ gap: 6, padding: 8, border: "1px solid var(--border)", borderRadius: 8 }}>
              {(["a", "b"] as const).map((side) => (
                <div key={side} className="ds-stack" style={{ gap: 4 }}>
                  <Select inputSize="sm" value={draft[side]} onChange={(e) => setDraft({ ...draft, [side]: e.target.value, [`${side}f`]: "" })} aria-label={side === "a" ? "First table" : "Second table"}>
                    <option value="">{side === "a" ? "First table…" : "Second table…"}</option>
                    {tables.filter((t) => t !== (side === "a" ? draft.b : draft.a)).map((t) => <option key={t} value={t}>{name(t)}</option>)}
                  </Select>
                  {draft[side] ? (
                    <Select inputSize="sm" value={draft[`${side}f`]} onChange={(e) => setDraft({ ...draft, [`${side}f`]: e.target.value })} aria-label="Column">
                      <option value="">Column…</option>
                      {fieldsOf(draft[side]).map((f) => <option key={f.source_name} value={f.source_name}>{f.display_name}</option>)}
                    </Select>
                  ) : null}
                </div>
              ))}
              <Checkbox label="Filter both ways" checked={draft.both} onChange={(v) => setDraft({ ...draft, both: v })} />
              <div className="ds-row" style={{ justifyContent: "flex-end" }}>
                <Button size="sm" onClick={() => setAdding(false)}>Cancel</Button>
                <Button size="sm" variant="primary" disabled={!draft.a || !draft.af || !draft.b || !draft.bf} onClick={() => { addLink({ fromDatasetId: draft.a, fromField: draft.af, toDatasetId: draft.b, toField: draft.bf, crossFilter: draft.both ? "both" : "single" }); setAdding(false); setDraft({ a: "", af: "", b: "", bf: "", both: true }); }}>Link</Button>
              </div>
            </div>
          ) : (
            <Button size="sm" icon={Plus} onClick={() => setAdding(true)} style={{ marginTop: 4 }}>Link two tables</Button>
          )}
        </>
      ) : null}
    </div>
  );
}
